import * as Sentry from "@sentry/react";

import axiosInstance from "../axios-instance";

type IxdUploadPolicy = {
  uploadId: string;
  url: string | null;
  fields: Record<string, string> | null;
};

const UPLOAD_FAILED_MESSAGE =
  "IXD 파일 업로드에 실패했습니다. 다시 시도해 주세요.";

/**
 * signed POST policy 로 파일을 GCS 에 직접 올린다.
 *
 * axiosInstance 는 우리 API 용 인증 헤더를 붙이는데 GCS 는 policy 서명만 봐야 하므로
 * 인증 헤더가 없는 XHR 로 보낸다. 진행률을 받을 수 있어 fetch 대신 XHR 을 쓴다.
 */
const postFileToGcs = (
  url: string,
  fields: Record<string, string>,
  file: File,
  onProgress?: (loadedBytes: number, totalBytes: number) => void,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => form.append(key, value));
    form.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded, event.total);
      };
    }
    // 폴백 없이 사용자에게 그대로 가는 종단 실패다. signed POST 는 policy 만료,
    // content-length-range 위반 등 여러 이유로 4xx 를 내는데 사용자 메시지는
    // 하나뿐이라, 원인 특정은 여기 남기는 맥락에만 의존한다.
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      Sentry.captureMessage("GCS signed POST 업로드 실패", {
        level: "error",
        extra: {
          status: xhr.status,
          response: xhr.responseText?.slice(0, 500),
          filename: file.name,
        },
      });
      reject(new Error(UPLOAD_FAILED_MESSAGE));
    };
    xhr.onerror = () => {
      Sentry.captureMessage("GCS signed POST 업로드 네트워크 실패", {
        level: "error",
        extra: { filename: file.name },
      });
      reject(new Error(UPLOAD_FAILED_MESSAGE));
    };
    xhr.send(form);
  });

/**
 * IXD 를 스테이징 슬롯에 올리고 uploadId 를 돌려준다.
 *
 * 기본은 GCS 직접 POST(1홉)다. 서명 가능한 GCS 가 없으면 서버 경유로 폴백하는데,
 * 이 경로는 Cloud Run 요청 상한(32MiB)에 걸리므로 로컬에서만 안전하다. 배포 환경에서
 * 폴백이 도는 것은 서명 권한/버킷 설정 오류이고 큰 IXD 만 413 으로 죽으므로,
 * 조용히 넘어가지 않고 Sentry 에 남긴다.
 *
 * `/v1/ixd-upload/policy` 와 폴백용 `/v1/ixd-upload/stage` 는 같은 백엔드 라우터라
 * policy 요청 실패는 폴백으로 흡수할 수 없다 — 백엔드 선배포가 전제다.
 *
 * 돌려받은 uploadId 로 각 흐름의 완료 API 를 호출하면 된다.
 */
export const uploadIxdToStagingSlot = async (
  file: File,
  onProgress?: (loadedBytes: number, totalBytes: number) => void,
): Promise<string> => {
  // 0바이트 File(빈 파일, 클라우드 동기화 placeholder 등)을 올리면 서버가 0KB blob 을
  // 만들어 비교/합성 단계에서 BadZipFile 로 터진다. 올리기 전에 여기서 끊는다.
  if (file.size === 0) {
    Sentry.captureMessage("0바이트 IXD 업로드 시도", {
      level: "warning",
      extra: { filename: file.name },
    });
    throw new Error(
      `빈 파일은 업로드할 수 없습니다: ${file.name}. 파일을 다시 확인해주세요.`,
    );
  }

  const policy: IxdUploadPolicy = (
    await axiosInstance.post("/v1/ixd-upload/policy")
  ).data;
  if (!policy.uploadId) {
    Sentry.captureMessage("IXD 업로드 policy 응답에 uploadId 가 없음", {
      level: "error",
      extra: { filename: file.name },
    });
    throw new Error(UPLOAD_FAILED_MESSAGE);
  }

  if (policy.url && policy.fields) {
    await postFileToGcs(policy.url, policy.fields, file, onProgress);
    return policy.uploadId;
  }

  if (!import.meta.env.DEV) {
    Sentry.captureMessage("배포 환경에서 IXD 서버 경유 폴백 진입", {
      level: "error",
      extra: { filename: file.name, size: file.size },
    });
  }
  const form = new FormData();
  form.append("upload_id", policy.uploadId);
  form.append("ixd_file", file);
  await axiosInstance.post("/v1/ixd-upload/stage", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (event) => {
      if (event.total) onProgress?.(event.loaded, event.total);
    },
  });
  return policy.uploadId;
};
